import { useState } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { useSortable, SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';
import IconArrow from '../../components/icons/IconArrow';
import IconDelete from '../../components/icons/IconDelete';
import IconEdit from '../../components/icons/IconEdit';
import ContainerLayout from '../../components/ContainerLayout';
import ContainerConfigForm from '../../components/ContainerConfigForm';
import BlockCard from './BlockCard';
import BlockTypePicker from './BlockTypePicker';
import { CONTAINER_LAYOUT_TYPE_LIST, getContainerLayoutType, safeParseContainerConfig } from '../../variables/containerLayoutTypes';
import { mergeBlocks, nextPosition, computeMove, BLOCK_KIND_STATIC } from './blockList';
import { buildElementPayload, buildInteractiveElementPayload, buildContainerPayload } from './exerciseTemplatePayload';
import { makeDndId, makeEmptyDropzoneId } from '../../components/dnd/dndId';
import { applyDragPreview } from '../../components/dnd/containerDragPreview';
import * as api from './api';

// A layout region holding its own scoped subset of the exercise's static +
// interactive blocks - mirrors courses/TopicContainerCard.jsx exactly (see
// that file's own comments for the reasoning behind every dnd-kit choice
// here), just against the exercise-templates API/entities and a 3-way
// static/interactive/container kind split instead of Topic's 2-way one.
export default function ContainerCard({ exerciseId, courseId, container, allElements, allInteractiveElements, canMoveUp, canMoveDown, onMove, onDeleted, onReload, dragPreview }) {
   const [configOpen, setConfigOpen] = useState(false);
   const [pickerOpen, setPickerOpen] = useState(false);
   const layoutType = getContainerLayoutType(container.layout) || CONTAINER_LAYOUT_TYPE_LIST[0];
   const config = safeParseContainerConfig(container.config, container.layout);

   // Containers never nest and never themselves get dragged (`disabled:
   // true`) - occupies its slot in the parent's top-level SortableContext
   // for correct neighbor-reflow math only. `rawChildren` (canonical)
   // drives move-button math; `children` (drag-preview-adjusted) drives
   // what's actually rendered.
   const { setNodeRef: setContainerSlotRef } = useSortable({ id: makeDndId('container', container.id), disabled: true });

   const { setNodeRef: setEmptyDropRef, isOver: isEmptyOver } = useDroppable({ id: makeEmptyDropzoneId(container.id) });

   const rawChildren = mergeBlocks(allElements, allInteractiveElements, [], container.id)
      .map((entry) => ({ ...entry, dndId: makeDndId(entry.kind, entry.block.id) }));
   const children = applyDragPreview(rawChildren, dragPreview, container.id);

   const handleDelete = () => {
      if (children.length > 0) {
         window.alert('Неможливо видалити контейнер: спочатку видаліть усі елементи всередині нього.');
         return;
      }
      if (!window.confirm('Видалити цей контейнер?')) return;
      api.deleteContainer(container.id).then(() => onDeleted()).catch(() => {
         window.alert('Не вдалося видалити контейнер.');
      });
   };

   const saveContainer = (changes) => {
      const payload = buildContainerPayload(exerciseId, container, changes);
      return api.updateContainer(container.id, payload).then(() => onReload());
   };

   const handleLayoutChange = (newLayout) => {
      const newType = getContainerLayoutType(newLayout);
      saveContainer({ layout: newLayout, config: JSON.stringify(newType.defaultConfig) });
   };

   const handleConfigChange = (nextConfig) => {
      saveContainer({ config: JSON.stringify(nextConfig) });
   };

   const handleAddBlock = (blockType) => {
      setPickerOpen(false);
      const position = nextPosition(rawChildren);
      const draft = { type: blockType.type, config: JSON.stringify(blockType.defaultConfig), position, container: container.id };
      const create = blockType.kind === BLOCK_KIND_STATIC
         ? api.createElement(buildElementPayload(exerciseId, draft))
         : api.createInteractiveElement(buildInteractiveElementPayload(exerciseId, draft));
      create.then(() => onReload()).catch((err) => console.error(err));
   };

   // Bound to rawChildren (canonical), not the drag-preview-adjusted
   // `children` - see TopicContainerCard.jsx's matching comment for why.
   const handleMoveChild = (index, direction) => {
      const swap = computeMove(rawChildren, index, direction);
      if (!swap) return;
      const saves = swap.map((entry) => {
         const payload = entry.kind === BLOCK_KIND_STATIC
            ? buildElementPayload(exerciseId, entry.block)
            : buildInteractiveElementPayload(exerciseId, entry.block);
         return entry.kind === BLOCK_KIND_STATIC
            ? api.updateElement(entry.block.id, payload)
            : api.updateInteractiveElement(entry.block.id, payload);
      });
      Promise.all(saves).then(() => onReload()).catch((err) => console.error(err));
   };

   return (
      <div ref={setContainerSlotRef} className='exercise-block-card topic-container-card' style={{ '--accent': '#7ec2e8' }}>
         <div className='exercise-block-card-toolbar'>
            <span className='exercise-block-card-type-label'>Контейнер: {layoutType.label}</span>
            <div className='exercise-block-card-actions'>
               <div className={'exercise-block-move-btn' + (!canMoveUp ? ' disabled' : '')} onClick={() => canMoveUp && onMove(-1)} title='Вгору'>
                  <IconArrow style={{ transform: 'rotate(-90deg)', height: 14, width: 14 }} />
               </div>
               <div className={'exercise-block-move-btn' + (!canMoveDown ? ' disabled' : '')} onClick={() => canMoveDown && onMove(1)} title='Вниз'>
                  <IconArrow style={{ transform: 'rotate(90deg)', height: 14, width: 14 }} />
               </div>
               <div className='exercise-block-edit-btn' onClick={() => setConfigOpen((prev) => !prev)} title='Налаштування'>
                  <IconEdit style={{ fill: '#7ec2e8', width: 14, height: 14, margin: 0 }} />
               </div>
               <div className='exercise-block-delete-btn' onClick={handleDelete} title='Видалити'>
                  <IconDelete style={{ fill: '#e0876c', width: 14, height: 14, margin: 0 }} />
               </div>
            </div>
         </div>

         {configOpen ? (
            <ContainerConfigForm layoutKey={layoutType.key} config={config}
               onLayoutChange={handleLayoutChange} onConfigChange={handleConfigChange} />
         ) : ''}

         <div className='exercise-block-card-body'>
            {rawChildren.length > 0 ? (
               <SortableContext items={children.map((entry) => entry.dndId)} strategy={rectSortingStrategy}>
                  <ContainerLayout layout={container.layout} config={config}>
                     {children.map((entry, index) => (
                        entry.isDropPlaceholder ? (
                           <div key='drop-placeholder' className='exercise-block-drop-placeholder' />
                        ) : (
                           <BlockCard key={entry.kind + '-' + entry.block.id} exerciseId={exerciseId} courseId={courseId} entry={entry}
                              canMoveUp={index > 0} canMoveDown={index < children.length - 1}
                              onMove={(direction) => handleMoveChild(index, direction)}
                              onDeleted={onReload} onSaved={onReload} />
                        )
                     ))}
                  </ContainerLayout>
               </SortableContext>
            ) : (
               // See TopicContainerCard.jsx's matching comment - the
               // droppable id stays registered regardless of whether a
               // preview is currently active, keyed off rawChildren (not
               // children.length) so it never flip-flops mid-hover.
               <div ref={setEmptyDropRef} className={'exercise-block-dropzone' + (isEmptyOver ? ' exercise-block-dropzone-over' : '')}>
                  {children.length > 0 ? (
                     <div className='exercise-block-drop-placeholder' />
                  ) : (
                     <p className='exercise-block-empty-hint'>Контейнер порожній - додайте блок нижче.</p>
                  )}
               </div>
            )}

            <BlockTypePicker isOpen={pickerOpen} onToggle={() => setPickerOpen((prev) => !prev)} onPick={handleAddBlock} />
         </div>
      </div>
   );
}
